import React, { useCallback, useMemo } from 'react';
import { FetchMoreContainer, StickyRowTable } from '@colonist/ui-platform';
import { T } from '@colonist/ui';
import { RankCell } from './RankCell/index.ts';
import { UsernameCell } from './UsernameCell/index.ts';
import { DivisionCell } from './DivisionCell/index.ts';
import { SkillRatingCell } from './SkillRatingCell/index.ts';
import { WinRateCell } from './WinRateCell/index.ts';
import { GamesPlayedCell } from './GamesPlayedCell/index.ts';
import styles from './RankedTable.module.scss';
import clsx from 'clsx';
const UNRANKED_HIDDEN_COLUMN_IDS = [
    'skillRating',
    'winRate',
    'totalGamesPlayed'
];
const UNRANKED_COLUMN_COLSPAN = UNRANKED_HIDDEN_COLUMN_IDS.length + 1;
export const RankedTable = ({ className, data, myRowData, fetchMoreData, moreDataAvailable, onClickMembershipIcon, onClickLocationFlag, onClickDivisionIcon, onClickRow, onClickLogin, unrankedMessage, emptyText, isUserOnDiscord })=>{
    const loginButton = onClickLogin == undefined ? undefined : /*#__PURE__*/ React.createElement("button", {
        className: clsx(styles.loginButton, styles.hideWhenCompact),
        onClick: (event)=>{
            event.stopPropagation();
            onClickLogin();
        }
    }, /*#__PURE__*/ React.createElement(T, {
        id: "strings:colonistCommon.rankedState.logInToPlayRanked"
    }));
    const messageNode = unrankedMessage == undefined ? undefined : /*#__PURE__*/ React.createElement(T, {
        id: unrankedMessage
    });
    const unrankedContent = loginButton !== null && loginButton !== void 0 ? loginButton : messageNode;
    const columns = useMemo(()=>{
        const isUnrankedMyRow = (row)=>row.myData && unrankedContent != undefined;
        return [
            {
                id: 'rank',
                header: ()=>/*#__PURE__*/ React.createElement(T, {
                        id: "strings:leaderboardPage.tableHeaders.rank"
                    }),
                overflow: 'nowrap',
                cell: ({ value, row })=>/*#__PURE__*/ React.createElement(RankCell, {
                        rank: value,
                        membershipType: row.membershipType,
                        onClickMembershipIcon: onClickMembershipIcon
                    })
            },
            {
                id: 'username',
                header: ()=>/*#__PURE__*/ React.createElement(T, {
                        id: "strings:leaderboardPage.tableHeaders.player"
                    }),
                className: styles.usernameColumnWidth,
                cell: ({ row })=>/*#__PURE__*/ React.createElement(UsernameCell, {
                        user: row,
                        onClickLocationFlag: onClickLocationFlag,
                        isUserOnDiscord: isUserOnDiscord
                    })
            },
            {
                id: 'division',
                header: ()=>/*#__PURE__*/ React.createElement(T, {
                        id: "strings:leaderboardPage.tableHeaders.division"
                    }),
                align: 'center',
                cell: ({ value, row })=>{
                    if (isUnrankedMyRow(row)) return unrankedContent;
                    return /*#__PURE__*/ React.createElement(DivisionCell, {
                        division: value,
                        subdivision: row.subdivision,
                        onClickDivisionIcon: onClickDivisionIcon
                    });
                }
            },
            {
                id: 'skillRating',
                header: ()=>/*#__PURE__*/ React.createElement(T, {
                        id: "strings:leaderboardPage.tableHeaders.rating"
                    }),
                align: 'right',
                cell: ({ value, row })=>{
                    if (isUnrankedMyRow(row)) return null;
                    return /*#__PURE__*/ React.createElement(SkillRatingCell, {
                        skillRating: value
                    });
                }
            },
            {
                id: 'winRate',
                header: ()=>/*#__PURE__*/ React.createElement(T, {
                        id: "strings:leaderboardPage.tableHeaders.winPercentage"
                    }),
                align: 'right',
                className: styles.hideWhenCompact,
                cell: ({ value, row })=>{
                    if (isUnrankedMyRow(row)) return null;
                    return /*#__PURE__*/ React.createElement(WinRateCell, {
                        winRate: value
                    });
                }
            },
            {
                id: 'totalGamesPlayed',
                header: ()=>/*#__PURE__*/ React.createElement(T, {
                        id: "strings:leaderboardPage.tableHeaders.games"
                    }),
                align: 'right',
                className: styles.hideWhenCompact,
                cell: ({ value, row })=>{
                    if (isUnrankedMyRow(row)) return null;
                    return /*#__PURE__*/ React.createElement(GamesPlayedCell, {
                        totalGamesPlayed: value
                    });
                }
            }
        ];
    }, [
        onClickMembershipIcon,
        onClickLocationFlag,
        onClickDivisionIcon,
        unrankedContent,
        isUserOnDiscord
    ]);
    const getCellProps = useCallback((row, columnId)=>{
        if (!row.myData) return undefined;
        if (unrankedContent == undefined) return undefined;
        if (columnId == 'division') return {
            colSpan: UNRANKED_COLUMN_COLSPAN,
            className: styles.unrankedMessage
        };
        if (UNRANKED_HIDDEN_COLUMN_IDS.includes(columnId)) return {
            className: styles.hiddenCell
        };
        return undefined;
    }, [
        unrankedContent
    ]);
    const getRowProps = useCallback(()=>({
            className: styles.rankedRow
        }), []);
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(className, styles.rankedTableWrapper),
        "data-testid": "leaderboard-ranking-table"
    }, /*#__PURE__*/ React.createElement(FetchMoreContainer, {
        onReachBottom: fetchMoreData,
        disabled: !moreDataAvailable
    }, /*#__PURE__*/ React.createElement(StickyRowTable, {
        className: styles.rankedTable,
        data: data,
        columns: columns,
        stickyRow: myRowData,
        getCellProps: getCellProps,
        getRowProps: getRowProps,
        onRowClick: onClickRow,
        emptyText: emptyText
    })));
};
